import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import { type ButtonHTMLAttributes, type MouseEvent, forwardRef } from "react";

import { cn } from "../../lib/cn";

/**
 * Motion (#2172, the laptop motion pass): a filled or bordered button
 * rises 1px onto a soft shadow under the pointer and its icon grows a
 * touch, so the hover reads at a glance; the press settles it and dips it
 * 3% (`press`, styles/tailwind.css). Ghost and link buttons only change
 * colour — they sit inside text and dense toolbars where a lift jitters.
 * Calm (D4): every curve eases out, nothing overshoots.
 *
 * The lift, the icon grow and the firmer border are scoped to pointers
 * that really hover (`@media (hover: hover)`): a touch screen keeps
 * `:hover` on the last thing tapped, so on a phone (the member app inside
 * Telegram uses this Button) a tapped *Send* would otherwise stay raised,
 * shadowed and with its icon at 110% until the next tap. The settle
 * (`active:`) sits in the same media query so it sorts after the lift and
 * wins while pressed; the 3% press dip (`press`) applies everywhere.
 * Literal class strings, so Tailwind's scanner finds them.
 *
 * Still water (#2496) gives the primary the site's recipe: the teal fill, a
 * 10 px radius (`rounded-lg`), weight 500, and `shadow-button` (a soft teal
 * shadow with a 25% light along the top edge) that drops to
 * `shadow-button-press` as the button is pressed. Secondary, outline and
 * destructive take the same radius. Ghost and link keep `rounded-md`: they
 * sit in dense toolbars. Two more variants:
 *
 *   - `glass`: the pane (`.glass`, tailwind.css) with ink text, for a button
 *     that floats over content. Opt in; S3 uses it on the phone.
 *   - `not-ready`: a mint fill that names the missing step in the caller's
 *     children, for example "Pick a day first", in place of a half-opacity
 *     button. It is `aria-disabled`, not `disabled`: it stays focusable so a
 *     screen reader reads the label, and a click does nothing, not even a
 *     form submit. No shadow, no lift, no press.
 *
 * A full-width primary (`w-full` in its className) adds `press-full`, which
 * lifts the press rule's `.w-full` skip for it alone (#2498); every full-width primary gets it, by decision; a caller may
 * add `press-dim` to dim as well. `opacity` is in the transition list so
 * that dim eases back on release instead of snapping.
 */
const LIFT =
  "[@media(hover:hover)]:hover:-translate-y-px [@media(hover:hover)]:hover:shadow [@media(hover:hover)]:[&:hover_svg]:scale-110 [@media(hover:hover)]:active:translate-y-0 [@media(hover:hover)]:active:shadow-sm";

/** The primary's lift: the shadow stays `shadow-button` and drops to the press shadow. */
const LIFT_PRIMARY =
  "[@media(hover:hover)]:hover:-translate-y-px [@media(hover:hover)]:hover:shadow-button [@media(hover:hover)]:[&:hover_svg]:scale-110 [@media(hover:hover)]:active:translate-y-0 [@media(hover:hover)]:active:shadow-button-press active:shadow-button-press";

const buttonStyles = cva(
  "press inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-[color,background-color,border-color,box-shadow,transform,translate,scale,opacity] duration-[var(--dur-med)] ease-[var(--ease-out)] active:duration-[var(--dur-press)] disabled:pointer-events-none disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg [&_svg]:transition-transform [&_svg]:duration-[var(--dur-med)] [&_svg]:ease-[var(--ease-out)]",
  {
    variants: {
      variant: {
        primary: `rounded-lg bg-accent-strong text-fg-on-accent hover:bg-accent shadow-button ${LIFT_PRIMARY}`,
        secondary: `rounded-lg bg-bg-subtle text-fg hover:bg-bg-elevated [@media(hover:hover)]:hover:border-fg-faint border border-border-strong ${LIFT}`,
        ghost: "rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg",
        outline: `rounded-lg border border-border-strong bg-transparent text-fg hover:bg-bg-subtle [@media(hover:hover)]:hover:border-fg-faint ${LIFT}`,
        destructive: `rounded-lg bg-danger text-fg-on-accent hover:bg-danger/90 shadow-sm ${LIFT}`,
        link: "rounded-md text-accent-fg underline-offset-4 hover:underline",
        glass:
          "glass glass-gloss rounded-lg text-fg [box-shadow:var(--glass-rim),var(--glass-shadow-sm)]",
        "not-ready": "cursor-default rounded-lg bg-accent-soft text-fg",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-9 px-4",
        lg: "h-10 px-5 text-base",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonStyles> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    // `not-ready` ignores the caller's click and never submits a form; it
    // stays focusable (aria-disabled, not `disabled`) so its label is read.
    const guard =
      variant === "not-ready"
        ? {
            "aria-disabled": true as const,
            onClick: (e: MouseEvent<HTMLButtonElement>) => e.preventDefault(),
          }
        : null;
    return (
      <Comp
        ref={ref}
        className={cn(
          buttonStyles({ variant, size }),
          // #2498: the press rule skips `.w-full` (tailwind.css); a
          // full-width primary opts back in so it scales as well as dims.
          (variant ?? "primary") === "primary" && /(^|\s)w-full(\s|$)/.test(className ?? "")
            ? "press-full"
            : null,
          className,
        )}
        {...props}
        {...guard}
      />
    );
  },
);
Button.displayName = "Button";
